// The shared Logto client owns tokens. Recovery clears only this portal's
// expired session; explicit user logout alone ends global SSO.

import axios, { CanceledError } from 'axios'
import { currentAuthEpoch, describeAuthFailure, isExpiredAuth } from '@/utils/authRecovery'
import { BASE_URL, isPortalRequest } from '@/environment/environment'
import { useAuthStore } from '@/modules/authStore/stores/authStore'
import { logtoClient } from '@/plugins/logto'
import router from '@/router'
import { ERouteNames } from '@/router/ERouteNames'
import type { ICustomAxiosRequestConfig } from '@/types/ICustomAxiosRequestConfig'
import { logger } from '@/utils/eventLogger'

axios.interceptors.response.use(
  // Прозрачно пропускаем успешные ответы
  response => response,
  error => {
    // Безопасное извлечение данных об ошибке
    const config = error.config as ICustomAxiosRequestConfig | undefined
    const method = config?.method?.toUpperCase() ?? 'UNKNOWN_METHOD'
    const url = config?.url ?? 'unknown URL'
    const errorMessage = error.response?.data?.message || error.message || 'Unknown error'
    const statusCode = error.response?.status || 'Unknown status'

    // Логирование ошибок только в режиме разработки
    if (import.meta.env.DEV) {
      console.log(
        'axiosInterceptors REJECT:',
        method,
        url,
        `Status: ${statusCode}`,
        `Error: ${errorMessage}`,
      )
    }

    // Ignore failures from an earlier sign-in/logout operation.
    if (error.response?.status === 401 && !config?.skipAuthLogout
      && config?.authEpoch === currentAuthEpoch()) {
      void useAuthStore().expireSession(`401 ${method} ${url}: ${errorMessage}`)
    }

    // 502 — страница технических работ (UX-флоу, не связан с авторизацией).
    // Гард currentRoute защищает от залпа параллельных 502 (NavigationDuplicated).
    if (error.response?.status === 502 && !config?.skipAuthLogout && router.currentRoute.value.name !== ERouteNames.SERVICE_WORKS) {
      void router.push({ name: ERouteNames.SERVICE_WORKS })
    }

    return Promise.reject(error)
  },
)

axios.interceptors.request.use(
  async (config: ICustomAxiosRequestConfig) => {

    if (config.skipAuth || !isPortalRequest(config.url, config.baseURL)) {
      return config
    }

    if (!logtoClient) {
      throw new Error('[Axios] Logto client не инициализирован: отсутствуют VITE_LOGTO_ENDPOINT или VITE_LOGTO_APP_ID')
    }

    config.authEpoch = currentAuthEpoch()
    try {
      const token = await logtoClient.getAccessToken(BASE_URL)
      if (config.authEpoch !== currentAuthEpoch()) {
        throw new CanceledError('Запрос отменён: сессия уже завершена')
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
        // Синхронизируем ID-токен с бэкендом при его изменении (избегая рекурсии для самого запроса синхронизации)
        if (config.url && !config.url.includes('/api/account/sync/')) {
          await useAuthStore().checkAndSyncIdToken()
        }
      }
      return config
    } catch (e) {
      if (config.authEpoch !== currentAuthEpoch()) throw e
      const reason = describeAuthFailure(e)
      if (!config.skipAuthLogout && config.authEpoch === currentAuthEpoch() && isExpiredAuth(e)) {
        void useAuthStore().expireSession(reason)
      } else {
        logger.error('[Auth] Ошибка получения токена', reason)
      }
      throw e
    }
  },
  error => Promise.reject(error),
)
